import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Lock, Server, Database, 
  CreditCard, MapPin, Eye, FileLock, Cloud, 
  Mail, Phone, ShieldCheck, Activity, Globe
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import Footer from '../components/Footer';
import HeaderActions from '../components/HeaderActions';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage(); // Dil fonksiyonunu çağır

  // Sayfa açıldığında en üste kaydır
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc] dark:bg-[#0f172a] transition-colors duration-500 font-sans pb-32">
      
      {/* Background Gradients */}
      <div className="fixed top-0 left-0 w-full h-[500px] bg-gradient-to-b from-emerald-50/50 to-transparent dark:from-emerald-900/10 dark:to-transparent pointer-events-none" />

      {/* ELITE PRIVACY HEADER */}
      <header className="bg-[#f8fafc]/90 dark:bg-[#0f172a]/90 backdrop-blur-2xl border-b border-slate-200 dark:border-white/5 px-6 py-6 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 bg-slate-100 dark:bg-white/5 rounded-xl hover:bg-emerald-600 hover:text-white transition-all text-slate-500 dark:text-slate-400"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-black tracking-tighter uppercase italic text-slate-900 dark:text-white leading-none flex items-center gap-3">
                <Shield size={24} className="text-emerald-600" /> {t('privacy_title') || 'Datenschutz'}
              </h1>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{t('privacy_status') || 'Status: High Security Level'} • 2025</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                <ShieldCheck size={14} className="text-emerald-600" />
                <span className="text-[9px] font-black text-emerald-600 uppercase tracking-tighter">{t('gdpr_compliant_badge') || 'GDPR / DSGVO Compliant'}</span>
              </div>
              <HeaderActions />
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 md:p-12 max-w-4xl mx-auto w-full space-y-10 relative z-10">
        
        {/* 1. OVERVIEW BENTO BOX */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-4"
        >
          <Card className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/5 shadow-xl rounded-[2.5rem] overflow-hidden">
            <CardContent className="p-8 md:p-10">
              <h2 className="text-lg font-black text-slate-900 dark:text-white mb-8 flex items-center gap-3 uppercase tracking-tight italic">
                <Eye size={20} className="text-blue-500" /> {t('priv_overview_title') || '1. Datenschutz auf einen Blick'}
              </h2>
              <div className="grid md:grid-cols-2 gap-10 text-sm leading-relaxed">
                <div className="space-y-4">
                  <p className="font-black text-blue-600 dark:text-blue-400 uppercase text-[10px] tracking-[0.2em]">{t('priv_protocol_title') || 'Sicherheits-Protokoll'}</p>
                  <p className="text-slate-600 dark:text-slate-400 font-medium">
                    {t('priv_protocol_text') || 'Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert.'}
                  </p>
                </div>
                <div className="space-y-4">
                  <p className="font-black text-orange-600 dark:text-orange-400 uppercase text-[10px] tracking-[0.2em]">{t('priv_collection_title') || 'Datenerfassung'}</p>
                  <p className="text-slate-600 dark:text-slate-400 font-medium italic">
                    "{t('priv_collection_text') || 'Daten werden entweder von Ihnen aktiv mitgeteilt oder automatisch durch unsere IT-Systeme erfasst.'}"
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* 2. RESPONSIBLE ENTITY - PREMIUM CARD */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <div className="bg-slate-900 dark:bg-[#0f172a] rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden group border border-slate-800 dark:border-white/5">
            <div className="relative z-10 flex flex-col md:flex-row justify-between gap-10">
              <div className="space-y-8">
                <div>
                  <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-500 mb-3 flex items-center gap-2">
                    <Globe size={12} /> {t('priv_responsible_title') || 'Verantwortliche Stelle'}
                  </h2>
                  <p className="text-4xl font-black tracking-tighter leading-none italic">Haydar Can Kotanoglu</p>
                  <p className="text-slate-400 font-bold mt-2 uppercase text-[10px] tracking-widest">BüroFuchs Digital Engineering</p>
                </div>
                <div className="space-y-3 text-sm font-medium text-slate-300">
                  <p className="flex items-center gap-3"><MapPin size={16} className="text-orange-500" /> Lilistraße 34, 63067 Offenbach, DE</p>
                  <p className="flex items-center gap-3"><Mail size={16} className="text-orange-500" /> info@burofuchs.de</p>
                  <p className="flex items-center gap-3"><Phone size={16} className="text-orange-500" /> +49 1575 3074362</p>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/10 flex flex-col justify-center items-center text-center group-hover:border-orange-500/30 transition-all duration-500">
                <FileLock size={48} className="mb-4 text-orange-500 animate-pulse" />
                <p className="text-[9px] font-black uppercase tracking-[0.2em] leading-tight text-slate-400">{t('priv_registry_no') || 'Data Protection Registry No'}<br/>2025-BF</p>
              </div>
            </div>
            <Shield size={260} className="absolute -right-20 -bottom-20 opacity-[0.03] group-hover:scale-110 transition-transform duration-1000 group-hover:rotate-12" />
          </div>
        </motion.section>

        {/* 3. INFRASTRUCTURE & CLOUD BENTO */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/5 shadow-lg rounded-[2.5rem] p-8 space-y-6 h-full">
              <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-2">
                <Server size={18} className="text-blue-500" /> {t('priv_hosting_title') || 'Hosting & Infrastruktur'}
              </h2>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <p className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight italic">AWS & Render Europe</p>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                    {t('priv_backend_text') || 'Unser Backend wird verschlüsselt in Frankfurt, Deutschland betrieben.'}
                  </p>
                </div>
                <div className="pt-6 border-t border-slate-100 dark:border-white/5">
                  <p className="text-xs font-black text-slate-900 dark:text-white mb-2 uppercase tracking-tight italic">{t('priv_frontend_title') || 'Vercel Global Edge'}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium italic opacity-70">
                    {t('priv_frontend_text') || 'Sichere Front-End-Auslieferung mit lokaler IP-Anonymisierung.'}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-[#020617] border border-white/5 shadow-2xl rounded-[2.5rem] p-8 space-y-8 relative overflow-hidden group h-full">
              <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] flex items-center gap-2">
                <Database size={18} className="text-orange-500" /> {t('priv_intelligence_title') || 'Intelligence Layer'}
              </h2>
              <div className="space-y-4 relative z-10">
                <div className="flex items-center gap-4 bg-white/5 p-5 rounded-3xl border border-white/5 group-hover:border-orange-500/20 transition-all duration-500">
                  <div className="p-3 bg-blue-500/10 rounded-2xl text-blue-400"><Cloud size={24} /></div>
                  <div>
                    <p className="text-[11px] font-black text-white uppercase tracking-widest">Google Gemini Pro</p>
                    <p className="text-[9px] text-slate-500 font-bold uppercase">{t('priv_no_training') || 'No Data Training Policy'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 p-5 rounded-3xl border border-white/5 group-hover:border-green-500/20 transition-all duration-500">
                  <div className="p-3 bg-green-500/10 rounded-2xl text-green-400"><Lock size={24} /></div>
                  <div>
                    <p className="text-[11px] font-black text-white uppercase tracking-widest">MongoDB Atlas</p>
                    <p className="text-[9px] text-slate-500 font-bold uppercase italic">{t('priv_db_enc') || 'AES-256 Cloud Encryption'}</p>
                  </div>
                </div>
              </div>
              <Activity size={100} className="absolute -right-10 -bottom-10 opacity-[0.03] group-hover:opacity-10 transition-opacity" />
            </Card>
          </motion.div>
        </div>

        {/* 4. STRIPE PAYMENT SECURE */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/5 rounded-[3rem] p-8 md:p-12 overflow-hidden relative group shadow-lg">
            <div className="flex flex-col md:flex-row items-center gap-10 relative z-10">
              <div className="p-6 bg-blue-600/10 rounded-[2rem] text-blue-600 dark:text-blue-400 shadow-xl group-hover:scale-105 transition-transform duration-500">
                <CreditCard size={40} strokeWidth={2.5} />
              </div>
              <div className="space-y-3 text-center md:text-left">
                <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight italic">{t('priv_payment_title') || '4. Sichere Zahlungsabwicklung'}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium max-w-xl">
                  {t('priv_payment_text') || 'Transaktionen werden via Stripe Infrastructure verschlüsselt verarbeitet. BüroFuchs speichert keine sensiblen Karteninformationen.'}
                </p>
              </div>
            </div>
            <div className="absolute top-0 right-0 h-full w-48 bg-gradient-to-l from-blue-500/5 to-transparent pointer-events-none" />
          </Card>
        </motion.section>

      </main>

      <div className="mt-10 pb-10"><Footer /></div>
    </div>
  );
};

export default PrivacyPage;